import Vue from "vue"
import { store } from "@/store/stores"
import userService from "@/services/userService"
import trainingFeatureAccess from "./trainingFeatureAccess"
import { healthDays, parseHealthHistory } from "./healthStatistics"
import { parseHealthDetails, parseSleepSessions } from "./healthDetails"

const messages = {
  HEALTH_ACCOUNT_CHANGED: "Die Anmeldung hat sich geändert. Bitte erneut verbinden.",
  HEALTH_CANCELLED: "Der Zugriff wurde beendet. Bitte erneut versuchen.",
  HEALTH_PERMISSION_REQUIRED: "Bitte die gewünschten Datenarten in Health Connect freigeben.",
  HEALTH_PERMISSIONS_CHANGED: "Die Freigaben wurden geändert. Bitte erneut synchronisieren.",
  HEALTH_SELECT_TYPES: "Bitte mindestens eine Datenart auswählen.",
  HEALTH_BUSY: "Die Freigabe in Health Connect ist noch geöffnet.",
  HEALTH_UNAVAILABLE: "Health Connect ist nicht verfügbar. Bitte installieren oder aktualisieren.",
  HEALTH_READ_FAILED: "Die Daten konnten nicht aktualisiert werden. Entsperre dein Handy und versuche es erneut.",
  HEALTH_WORKOUT_UNAVAILABLE: "Diese Aufzeichnung ist nicht mehr verfügbar oder noch nicht abgeschlossen.",
  HEALTH_SETTINGS_UNAVAILABLE: "Öffne Health Connect in den Android-Einstellungen.",
  HEALTH_INVALID_RANGE: "Bitte einen gültigen Zeitraum von höchstens einem Monat auswählen.",
  HEALTH_HISTORY_UNAVAILABLE: "Ältere Zeiträume werden von dieser Health-Connect-Version noch nicht unterstützt.",
}

export function healthSnapshot(raw) {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw.day) || !Number.isFinite(raw.syncedAt)) throw new Error(messages.HEALTH_READ_FAILED)
  const daily = {}
  for (const key of ["steps", "activeEnergyKilocalories", "totalEnergyKilocalories", "distanceMeters", "sleepSeconds", "sleepWindowStart", "weightKilograms", "weightMeasuredAt", "heartRate", "heartRateMeasuredAt"]) {
    const value = raw.daily?.[key]
    if (typeof value === "number" && Number.isFinite(value) && value >= 0) daily[key] = value
  }
  const seen = new Set()
  const workouts = (Array.isArray(raw.workouts) ? raw.workouts : []).filter((item) => {
    if (item?.provider !== "health_connect" || !/^hc:[A-Za-z0-9_-]{1,342}$/.test(item.workoutId) || seen.has(item.workoutId)) return false
    seen.add(item.workoutId)
    return true
  }).slice(0, 200)
  return { day: raw.day, syncedAt: raw.syncedAt, daily, workouts, truncated: raw.truncated === true }
}

class HealthConnectService {
  constructor() {
    this.state = Vue.observable({ android: false, supported: false, status: "checking", enabled: false, ownerId: "",
      categories: [], grantedCategories: [], missingCategories: [],
      lastSyncAt: null, permissionKey: "", snapshot: null, busy: false, error: "",
      historySupported: false, historyPermissionAvailable: false, historyPermissionGranted: false,
      history: null, historyBusy: false, historyError: "",
      detailsSupported: false, details: null, detailsBusy: false, detailsError: "" })
    this.epoch = 0
    this.historyRequest = 0
    this.detailsRequest = 0
    this.owner = ""
    this.handleReady = () => {
      this.lastRefresh = 0
      if (this.state.busy) this.refreshAfter = true
      else this.refresh(true)
    }
    this.handleForeground = () => { if (document.visibilityState === "visible" && !this.state.busy) this.refresh() }
    this.handleOpen = () => this.open()
  }

  isAndroid() {
    try { if (window.localStorage?.getItem("isAndroid") === "true") return true }
    catch (_) { /* Device startup storage is optional; the native bridge still verifies support. */ }
    return /Android/i.test(window.navigator?.userAgent || "")
  }
  currentOwner() { return String(userService.getUserId() || "") }

  async start(router) {
    if (this.unsubscribe) return
    this.router = router
    await store.restored
    this.unsubscribe = store.subscribe(() => {
      const owner = trainingFeatureAccess.isAllowed() ? this.currentOwner() : ""
      if (this.owner !== owner) { this.reset(owner); this.refresh() }
    })
    this.unsubscribeAccess = trainingFeatureAccess.subscribe(() => this.refresh(true))
    window.addEventListener("flutterInAppWebViewPlatformReady", this.handleReady)
    window.addEventListener("healthImportBridgeReady", this.handleReady)
    window.addEventListener("trainingLiveForeground", this.handleForeground)
    window.addEventListener("healthConnectOpen", this.handleOpen)
    document.addEventListener("visibilitychange", this.handleForeground)
    await this.refresh()
  }

  stop() {
    this.unsubscribe?.(); this.unsubscribe = null
    this.unsubscribeAccess?.(); this.unsubscribeAccess = null
    window.removeEventListener("flutterInAppWebViewPlatformReady", this.handleReady)
    window.removeEventListener("healthImportBridgeReady", this.handleReady)
    window.removeEventListener("trainingLiveForeground", this.handleForeground)
    window.removeEventListener("healthConnectOpen", this.handleOpen)
    document.removeEventListener("visibilitychange", this.handleForeground)
    this.reset("")
  }

  reset(owner) {
    this.epoch++; this.owner = owner; this.lastRefresh = 0
    this.clearHistory()
    Object.assign(this.state, { enabled: false, ownerId: owner, categories: [], grantedCategories: [], missingCategories: [],
      lastSyncAt: null, permissionKey: "", snapshot: null, busy: false, error: "",
      historySupported: false, historyPermissionAvailable: false, historyPermissionGranted: false, detailsSupported: false })
  }

  async native(method, payload = {}, timeoutMs = 30000) {
    const cleanup = method === "healthConnectConfigure" && payload.userId === ""
    const access = trainingFeatureAccess.capture()
    if (access === null && !cleanup) return { supported: false }
    const token = window.__wellcomeHealthImportToken
    const currentOwner = this.currentOwner()
    const owner = cleanup ? "" : currentOwner
    const epoch = this.epoch
    if (!this.isAndroid() || !token || !window.flutter_inappwebview?.callHandler) return { supported: false }
    let timer
    try {
      const result = await Promise.race([
        window.flutter_inappwebview.callHandler(method, { ...payload, userId: owner, bridgeToken: token }),
        new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(messages.HEALTH_READ_FAILED)), timeoutMs) }),
      ])
      if (epoch !== this.epoch || currentOwner !== this.currentOwner() || token !== window.__wellcomeHealthImportToken || (!cleanup && !trainingFeatureAccess.isCurrent(access))) throw new Error(messages.HEALTH_ACCOUNT_CHANGED)
      // A supported bridge may report a read/permission error on its first call.
      // Keep that error visible instead of treating the installed app as unsupported.
      if (result?.supported === true) this.state.supported = true
      if (result?.accepted === false) throw new Error(messages[result.code] || messages.HEALTH_READ_FAILED)
      return result || { supported: false }
    } finally { clearTimeout(timer) }
  }

  applyStatus(result) {
    if (result.supported !== true) {
      this.clearHistory()
      Object.assign(this.state, { supported: false, status: "app_update_required", snapshot: null, enabled: false,
        historySupported: false, historyPermissionAvailable: false, historyPermissionGranted: false, detailsSupported: false })
      return
    }
    if (result.permissionKey !== this.state.permissionKey || result.enabled !== true) {
      this.state.snapshot = null
      this.clearHistory()
    }
    Object.assign(this.state, { supported: true, status: result.status, enabled: result.enabled === true,
      historySupported: result.historySupported === true,
      detailsSupported: result.detailsSupported === true,
      historyPermissionAvailable: result.historyPermissionAvailable === true,
      historyPermissionGranted: result.historyPermissionGranted === true,
      categories: result.categories || [], grantedCategories: result.grantedCategories || [], missingCategories: result.missingCategories || [],
      lastSyncAt: result.lastSyncAt || null, permissionKey: result.permissionKey || "" })
    if (!this.state.historySupported) this.clearHistory()
    if (!this.state.detailsSupported) this.clearDetails()
  }

  async refresh(force = false) {
    this.state.android = this.isAndroid()
    if (!this.state.android) return
    if (!trainingFeatureAccess.isAllowed()) {
      this.reset("")
      if (trainingFeatureAccess.state.ready) await this.clear()
      return
    }
    const owner = this.currentOwner()
    if (this.owner !== owner) this.reset(owner)
    if (this.state.busy || (!force && Date.now() - (this.lastRefresh || 0) < 30000)) return
    const epoch = this.epoch
    this.state.busy = true; this.state.error = ""
    try {
      const result = await this.native("healthConnectConfigure")
      this.applyStatus(result)
      if (owner && result.enabled && result.grantedCategories?.length) await this.sync()
      this.lastRefresh = Date.now()
    } catch (error) {
      if (epoch === this.epoch) {
        this.clearHistory()
        this.state.snapshot = null; this.state.error = error.message
        if (this.state.supported) this.state.status = "error"
      }
    } finally {
      if (epoch === this.epoch) {
        this.state.busy = false
        if (this.refreshAfter) { this.refreshAfter = false; this.refresh(true) }
      }
    }
  }

  async sync() {
    if (!trainingFeatureAccess.isAllowed()) { this.reset(""); return }
    const result = await this.native("healthConnectSync")
    if (!result.supported) throw new Error("Bitte die aktuelle Android-App installieren.")
    // Replace the snapshot. Repeated reads never append duplicate workouts or steps.
    this.state.snapshot = healthSnapshot(result.snapshot)
    this.state.lastSyncAt = this.state.snapshot.syncedAt
  }

  clearHistory() {
    this.historyRequest++
    Object.assign(this.state, { history: null, historyBusy: false, historyError: "" })
    this.clearDetails()
  }

  clearDetails() {
    this.detailsRequest++
    Object.assign(this.state, { details: null, detailsBusy: false, detailsError: "" })
  }

  async loadDetails(day) {
    if (!trainingFeatureAccess.isAllowed() || !this.state.enabled || !this.state.detailsSupported) { this.clearDetails(); return }
    let request = ++this.detailsRequest
    const epoch = this.epoch
    Object.assign(this.state, { details: null, detailsBusy: true, detailsError: "" })
    try {
      healthDays(day, day)
      const status = await this.native("healthConnectConfigure")
      if (request !== this.detailsRequest || epoch !== this.epoch) return
      this.applyStatus(status)
      request = this.detailsRequest
      if (!this.state.enabled || !this.state.detailsSupported) return
      this.state.detailsBusy = true
      const permissionKey = this.state.permissionKey
      const result = await this.native("healthConnectDetails", { day }, 45000)
      if (request !== this.detailsRequest || epoch !== this.epoch || permissionKey !== this.state.permissionKey) return
      if (!result.supported) throw new Error("Für Schlafphasen und Pulsverläufe brauchst du die aktuelle Android-App.")
      this.state.details = parseHealthDetails(result, day, this.state.grantedCategories)
    } catch (error) {
      if (request === this.detailsRequest && epoch === this.epoch) {
        this.state.details = null
        this.state.detailsError = error.message
      }
    } finally {
      if (request === this.detailsRequest && epoch === this.epoch) this.state.detailsBusy = false
    }
  }

  async loadHistory(range) {
    if (!trainingFeatureAccess.isAllowed() || !this.state.enabled || !this.state.historySupported) { this.clearHistory(); return }
    let request = ++this.historyRequest
    const epoch = this.epoch
    Object.assign(this.state, { history: null, historyBusy: true, historyError: "" })
    try {
      healthDays(range.from, range.to)
      // Recheck native permissions before every period, including returning from Android settings.
      const status = await this.native("healthConnectConfigure")
      if (request !== this.historyRequest || epoch !== this.epoch) return
      this.applyStatus(status)
      request = this.historyRequest
      if (!this.state.enabled || !this.state.historySupported) return
      this.state.historyBusy = true
      const permissionKey = this.state.permissionKey
      const result = await this.native("healthConnectHistory", { from: range.from, to: range.to }, 45000)
      if (request !== this.historyRequest || epoch !== this.epoch || permissionKey !== this.state.permissionKey) return
      if (!result.supported) throw new Error("Für den Verlauf brauchst du die aktuelle Android-App.")
      const history = parseHealthHistory(result, range)
      history.sleepSessions = this.state.grantedCategories.includes("sleep") && Array.isArray(result.sleepSessions)
        ? parseSleepSessions(result.sleepSessions, -Infinity, Infinity).filter(session => history.days.some(day => day.day === session.day && day.readable)) : []
      history.sleepTruncated = result.sleepTruncated === true
      this.state.history = history
    } catch (error) {
      if (request === this.historyRequest && epoch === this.epoch) {
        this.state.history = null
        this.state.historyError = error.message
      }
    } finally {
      if (request === this.historyRequest && epoch === this.epoch) this.state.historyBusy = false
    }
  }

  async connect(categories, history = false) {
    if (!trainingFeatureAccess.isAllowed()) return
    if (this.state.busy) return
    const epoch = this.epoch
    this.clearHistory()
    this.state.busy = true; this.state.error = ""; this.state.snapshot = null
    try {
      this.applyStatus(await this.native("healthConnectConfigure"))
      this.applyStatus(await this.native("healthConnectAuthorize", { categories, history: history === true }, 300000))
      if (this.state.enabled) await this.sync()
    } catch (error) { if (epoch === this.epoch) this.state.error = error.message }
    finally { if (epoch === this.epoch) this.state.busy = false }
  }

  async disconnect() {
    if (!trainingFeatureAccess.isAllowed()) return
    // Invalidate old JS answers immediately; the native bridge invalidates its reads too.
    this.reset(this.currentOwner())
    this.state.busy = true
    const epoch = this.epoch
    try {
      const result = await this.native("healthConnectDisconnect")
      this.applyStatus(result)
      if (result.permissionRevocationFailed) this.state.error = "Verbindung getrennt. Bitte die Systemfreigaben zusätzlich in Health Connect entfernen."
    } catch (error) { if (epoch === this.epoch) this.state.error = error.message }
    finally { if (epoch === this.epoch) this.state.busy = false }
  }

  async clear() {
    this.reset("")
    try { await this.native("healthConnectConfigure", { userId: "" }, 1500) } catch (_) { /* Navigation also invalidates native reads. */ }
  }

  async settings() {
    try { await this.native("healthConnectSettings") } catch (error) { this.state.error = error.message }
  }
  async importAvailable() {
    if (!trainingFeatureAccess.isAllowed()) return false
    try {
      if (this.owner !== this.currentOwner()) this.reset(this.currentOwner())
      this.applyStatus(await this.native("healthConnectConfigure"))
      return this.state.enabled && this.state.grantedCategories.includes("workouts")
    } catch (_) { return false }
  }
  async open() {
    if (!trainingFeatureAccess.isAllowed() || !this.router || !this.currentOwner()) return
    await this.router.push({ path: "/workout/hub", query: { health: "1" } }).catch(() => {})
    await Vue.nextTick()
    document.getElementById("android-health-connect")?.scrollIntoView({ block: "start", behavior: "smooth" })
  }
}

export default new HealthConnectService()
